/**
 * What the palette shows for a query. Pure: the palette loads the lists, this
 * decides which rows earn a place and in what order.
 *
 * Destinations are few and always eligible. Records are many, so a record
 * group appears only once the query has QUERY_MIN characters and keeps its
 * best few, ranked by where the query falls: a name that starts with it, then
 * a name that contains it, then any other field (an email, an id, a status)
 * that contains it.
 */
import type { RecordRef } from './record.js';

export const QUERY_MIN = 2;
export const GROUP_LIMIT = 8;

export interface PaletteEntry {
  /** Unique within the palette. */
  key: string;
  icon: string;
  label: string;
  /** Mono text at the right edge: a status, a kind, a role, a path. */
  hint?: string | undefined;
  /** Other fields the query may match: an email, an id. */
  fields?: readonly string[] | undefined;
}

export interface PaletteRecord extends PaletteEntry {
  ref: RecordRef;
}

export interface PaletteDestination extends PaletteEntry {
  to: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 0 is the best place for the query to fall; null is no match. */
function rank(entry: PaletteEntry, q: string): number | null {
  const label = entry.label.toLowerCase();
  if (label.startsWith(q)) return 0;
  if (label.includes(q)) return 1;
  if ((entry.fields ?? []).some((f) => f.toLowerCase().includes(q))) return 2;
  if (entry.hint && entry.hint.toLowerCase().includes(q)) return 3;
  return null;
}

/** The entries the query matches, best first, at most `limit`; ties keep list order. */
export function paletteMatches<T extends PaletteEntry>(
  entries: readonly T[],
  query: string,
  limit: number = Number.POSITIVE_INFINITY,
): T[] {
  const q = query.trim().toLowerCase();
  if (q === '') return entries.slice(0, limit);
  return entries
    .map((entry, index) => ({ entry, index, at: rank(entry, q) }))
    .filter((r): r is { entry: T; index: number; at: number } => r.at !== null)
    .sort((a, b) => a.at - b.at || a.index - b.index)
    .slice(0, limit)
    .map((r) => r.entry);
}

/** Whether the query is long enough for record groups to appear. */
export function searchesRecords(query: string): boolean {
  return query.trim().length >= QUERY_MIN;
}

/** The task id in the query, when the whole query is one UUID. */
export function taskIdIn(query: string): string | null {
  const q = query.trim();
  return UUID.test(q) ? q.toLowerCase() : null;
}
